import { Tab } from '../../../common/TabSelector/TabSelector';
import { defaultColors } from '../graphColorData';
import { chartInitData } from '../transformer';
import { graphDataType } from '../types';
import CONSTANTS from './constants';
import { CHART_COLORS } from './creditBureau_graphColorData';
import { CMRCreditSummaryData, CreditHighlightsData, CreditSummaryOfKeyOfficialsData } from './types';

interface CibilOverview {
    totalActiveAccounts: number | null;
    totalSanctionedAmount: number | null;
    creditUtilisation: number | null;
    enquiriesInPast90Days: number | null;
    noOfDpdAccounts: number | null;
    totalOverdueAmount: number | null;
    highestDpdRecorded: number | null;
    creditScoreMin: number | null;
    creditScoreMax: number | null;
    riskCategory: string | null;
    noOfOfficials: number | null;
    totalDpdsRecorded: number | null;
}

interface DirectorsOverviewResponse {
    name: string | null;
    totalAccounts: number | null;
    dpdAccounts: number | null;
    currentOverdue: number | null;
    maxDpds: number | null;
    cibilScore: number | null;
    directorId: string | null;
    creditBureau: string | null;
    designation?: string | null;
}
interface CreditBureauReducer {
    isLoading: boolean;
    loadingDirectorsOverviewList: boolean;
    mainData: {
        cmrCreditSummary: null | CMRCreditSummaryData;
        listDirectorsOverview: DirectorsOverviewResponse[];
        // person-grouped shape (grouping owned by polus); All view renders this, others keep using the flat list
        groupedDirectors: any[];
        creditHighlights: null | CreditHighlightsData;
        chargeDetails: any;
    };
    charts: {} | graphDataType[];
    cibilOverviewData: CibilOverview | null;
    latestConsents: Record<string, { consenterName: string; consentedAt: string }>;
    // True once FETCH_LATEST_CONSENTS_SUCCESS has fired at least once for the current
    // session — consumers must gate their "No Consent Given" fallback behind this flag
    // so the empty initial state doesn't render a false negative before the fetch settles.
    isLatestConsentsLoaded: boolean;
    // Per-PNO "consent mandatory" config (RECUR-27297), carried from the same
    // getConsentDetails response as latestConsents. Initialised false so the FE fails
    // open in parity with the BE — the BE gate is the single authority; this flag only
    // controls whether the Fetch/Refetch button renders disabled in the meantime.
    consentRequired: boolean;
}

const inititalState: CreditBureauReducer = {
    isLoading: false,
    loadingDirectorsOverviewList: false,
    mainData: {
        cmrCreditSummary: null,
        listDirectorsOverview: [],
        groupedDirectors: [],
        creditHighlights: null,
        chargeDetails: null,
    },
    charts: {},
    cibilOverviewData: null,
    latestConsents: {},
    isLatestConsentsLoaded: false,
    consentRequired: false,
};

const initializeChartsData = (state, action) => {
    let chartsEnum = {};

    action.payload?.chartPanel?.sections?.forEach((section) => {
        section?.cards?.forEach((chart) => {
            const colors = CHART_COLORS?.[chart] || defaultColors;
            chartsEnum[chart] = { ...chartInitData, colors };
        });
    });

    return {
        ...state,
        charts: chartsEnum,
    };
};

const fetchChargesDataSuccess = (state, action) => {
    return {
        ...state,
        mainData: {
            ...state?.mainData,
            chargeDetails: action?.payload,
        },
    };
};

const fetchChartDataSuccess = (state, action) => {
    const graphName = action.payload.graphName;

    return {
        ...state,
        charts: {
            ...state.charts,
            [graphName]: {
                ...state?.charts?.[graphName],
                isLoading: false,
                isFetched: true,
                ...action.payload.data,
            },
        },
    };
};

const fetchChartDataError = (state, action) => {
    const graphName = action?.payload?.graphNames?.[0];
    return {
        ...state,
        charts: {
            ...state.charts,
            [graphName]: {
                ...state.charts?.graphName,
                isLoading: false,
            },
        },
    };
};

const fetchCibilOverviewSuccess = (state, action) => {
    return {
        ...state,
        cibilOverviewData: action?.payload?.cibilOverviewData,
    };
};

const setLoadingDirectorOverview = (state, action) => {
    return {
        ...state,
        loadingDirectorsOverviewList: action.payload,
    };
};

const fetchDirectorsOverviewSuccess = (state, action) => {
    return {
        ...state,
        loadingDirectorsOverviewList: false,
        mainData: {
            ...state?.mainData,
            listDirectorsOverview: action?.payload?.listDirectorsOverview || [],
            groupedDirectors: action?.payload?.groupedDirectors || [],
        },
    };
};

const fetchLatestConsentsSuccess = (state: CreditBureauReducer, action: any): CreditBureauReducer => {
    return {
        ...state,
        latestConsents: action?.payload?.consentByDirectorId || {},
        isLatestConsentsLoaded: true,
        consentRequired: !!action?.payload?.consentRequired,
    };
};

const reducer = (state: CreditBureauReducer = inititalState, action: any): CreditBureauReducer => {
    switch (action.type) {
        case CONSTANTS.INITIALIZE_CHARTS_DATA:
            return initializeChartsData(state, action);
        case CONSTANTS.FETCH_CHART_DATA_SUCCESS:
            return fetchChartDataSuccess(state, action);
        case CONSTANTS.FETCH_CHART_DATA_ERROR:
            return fetchChartDataError(state, action);
        case CONSTANTS.FETCH_CIBIL_OVERVIEW_SUCCESS:
            return fetchCibilOverviewSuccess(state, action);

        case CONSTANTS.SET_LOADING_DIRECTOR_OVERVIEW:
            return setLoadingDirectorOverview(state, action);
        case CONSTANTS.FETCH_DIRECTOR_OVERVIEW_SUCCESS:
            return fetchDirectorsOverviewSuccess(state, action);
        case CONSTANTS.FETCH_CHARGE_DETAILS_SUCCESS:
            return fetchChargesDataSuccess(state, action);
        case CONSTANTS.FETCH_LATEST_CONSENTS:
            // Reset before the call so a stale `true` from a previous org (or a request that
            // then fails / returns non-SUCCESS) can never survive — fail-open must hold at
            // every step, not just on a successful response.
            return { ...state, consentRequired: false };
        case CONSTANTS.FETCH_LATEST_CONSENTS_SUCCESS:
            return fetchLatestConsentsSuccess(state, action);
        default:
            return state;
    }
};

export default reducer;
